/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Button, Tooltip, IconButton, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setHousingType, nextStep, prevStep, setStepVerified } from '../habitationSlice';
import { HousingType } from '../types';
import InfoText from '../components/InfoText'; // adapte le chemin si besoin

const OptionBox = styled(Box)<{ selected: boolean }>`
  border: 2px solid ${({ selected }) => (selected ? '#2F6472' : '#ccc')};
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  position: relative;
  height: 100%;
  width: 230px;
  @media (max-width: 600px) {
    width: 150px;
    height: 150px;
  }

  &:hover {
    border-color: #2F6472;
  }
`;

const Image = styled.img`
  height: 105px;
  @media (max-width: 600px) {
    height: 60px;
  }
`

const ButtonGroup = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`;


const ErrorText = styled.p`
  color: red;
  font-size: 0.9rem;
  margin-top: 8px;
  text-align: center;
`;

const HousingTypeStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('habitation');
  const [error, setError] = useState('');
  const selected = useAppSelector((state) => state.habitation.housingType);

  const handleSelect = (type: HousingType) => {
    dispatch(setHousingType(type));
  };



  const handleNext = () => {
    if (selected == undefined || selected == null) {
      setError(t('step2.type.error') || 'Merci de renseigner le type de logement.');
      return;
    }
    else {
        setError('');
    }
    dispatch(setStepVerified(2));
    dispatch(nextStep());
  };

  return (
    <Box sx={{ maxWidth: 800, margin:{ xs: '0 auto', sm: '0 100px'}, padding: 2 }}>
      <Typography variant="subtitle1" gutterBottom mb={0}>
        {t('step2.title')}
      </Typography>
      <InfoText>{t('step2.info')}</InfoText>
      <Grid container spacing={1} sx={{ mt: 2, minHeight: '100px' }}>
        <Grid item xs={6} sm={6} container justifyContent="left" alignItems="center">
          <OptionBox selected={selected === HousingType.room} onClick={() => handleSelect(HousingType.room)}>
            <Tooltip title={t('step2.info')} placement="top" arrow>
              <IconButton size="small" sx={{ position: 'absolute', top: 10, right: 10 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Image src="/images/bed-icon.png" alt="chambre" />
            <Typography sx={{textTransform: 'uppercase'}} mt={{xs:0, sm: 2}}>{t('step2.room')}*</Typography>
          </OptionBox>
        </Grid>

        <Grid item xs={6} sm={6} container justifyContent="right" alignItems="center">
          <OptionBox selected={selected === HousingType.full} onClick={() => handleSelect(HousingType.full)}>
            <Image src="/images/home-icon.png" alt="logement entier" />
            <Typography sx={{textTransform: 'uppercase'}}  mt={{xs:0, sm: 2}}>{t('step2.entire')}</Typography>
          </OptionBox>
        </Grid>
      </Grid>

      {error && <ErrorText>{error}</ErrorText>}

      <ButtonGroup>
        <Button onClick={() => dispatch(prevStep())} variant="outlined" sx={{ borderRadius: 20, px: 4 }}>
          {t('step1.back')}
        </Button>
        <Button
          onClick={() => handleNext()}
          variant="contained"
          sx={{
            textTransform: 'uppercase',
            borderRadius: 20,
            px: 4,
            backgroundColor: '#2F6472',
            '&:hover': { backgroundColor: 'white', color: '#2F6472' }
          }}
          disabled={selected == undefined || selected == null}
        >
          {t('step1.next')}
        </Button>
      </ButtonGroup>
    </Box>
  );
};

export default HousingTypeStep;
